import React, { useEffect, useState, useMemo } from 'react';
import { toast } from "react-toastify";
import { getData } from '../../../webService/webservice.js';
import { useStore } from '../../../Context/context.jsx';
import { Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import StoreSingleAllStoresSelect, { ALL_STORE_OPTION } from "../../Challange/StoreSingleAllStoresSelect.jsx";
const ExpenseHeader = ({ onDataFetched, totals, currentPage, limit, searchQuery, refreshKey, onPageReset, sortField, sortOrder }) => {
    const role = localStorage.getItem("role");
    const adminStoreIds = JSON.parse(localStorage.getItem("storeIds") || "[]");
    const initialStoreId = adminStoreIds[0] || "";
    const sessionToken = localStorage.getItem("Token");
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const { selectedStoreId, setSelectedStoreId } = useStore();
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

    const handleDateChange = (type, value) => {
        if (!value) {
            if (type === "start") setStartDate("");
            if (type === "end") setEndDate("");
            return;
        }
        const year = value.split("-")[0];
        if (year.length > 4) {
            toast.error("Year should contain only 4 digits");
            return;
        }
        if (type === "start") {
            if (endDate && new Date(value) > new Date(endDate)) {
                toast.error("Start date cannot be after end date");
                return;
            }
            setStartDate(value);
        }
        if (type === "end") {
            if (startDate && new Date(value) < new Date(startDate)) {
                toast.error("End date cannot be before start date");
                return;
            }
            setEndDate(value);
        }
    };

    const [store, setStore] = useState(ALL_STORE_OPTION);
    const fetchExpense = async (page = currentPage, filterValue = filter) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: limit === "all" ? 999999 : limit,
                filter: filterValue,
                search: searchQuery
            };
            if (filterValue === "custom") {
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
            }
            params.sort = JSON.stringify([[sortField, sortOrder]]);
            const sid = ["SuperAdmin", "SelfAdmin", "Manager", "Accountant"].includes(role) ? store?.value : Array.isArray(selectedStoreId)
                ? selectedStoreId[0]
                : selectedStoreId;
            if (sid && sid.length === 24) {
                params.storeId = sid;
            }
            // if (selectedStoreId) params.storeId = selectedStoreId;
            const response = await getData("expense/expenses", params, sessionToken);

            if (response?.status !== 200) {
                toast.error(response.message || "Failed to fetch Expenses");
                return;
            }

            const resultsWrapper = response?.data?.[0];
            const ExpenseList = resultsWrapper?.paginatedResults || [];
            const totalCount = resultsWrapper?.totalCount || 0;
            const totalPages = resultsWrapper?.totalPages || 1;
            const fetchedTotals = resultsWrapper?.totals || {};

            // send data back to parent
            onDataFetched(ExpenseList, fetchedTotals, totalCount, totalPages);
        } catch (error) {
            toast.error(error.message || "Failed to fetch Expenses");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpense(1, filter, selectedStoreId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, searchQuery, limit, refreshKey, selectedStoreId, store, sortField, sortOrder, startDate, endDate]);

    useEffect(() => {
        fetchExpense(currentPage, filter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        const handleStoreChange = () => {
            const storeId = selectedStoreId;
            setSelectedStoreId(storeId);
            if (onPageReset) onPageReset();
        };
        window.addEventListener("storeChanged", handleStoreChange);
        return () => window.removeEventListener("storeChanged", handleStoreChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onPageReset]);

    return (
        <>
            <div className='Expense-Eidgets'>
                {/* ── Row 1: Title + Store selector ── */}
                <div className='Expense'>
                    <Typography variant="h6" sx={{ mb: 0, fontWeight: 700, color: '#3C0008' }}>
                        Expenses
                    </Typography>
                    {["SuperAdmin", "SelfAdmin", "Manager", "Accountant"].includes(role) && (
                        <div style={{ minWidth: 220, flex: '0 1 auto' }}>
                            <StoreSingleAllStoresSelect
                                value={store}
                                onChange={(store) => setStore(store)}
                            />
                        </div>
                    )}
                </div>

                {/* ── Row 2: Filter buttons (Desktop) ── */}
                <div className="Expense-header-actions desktop-only">
                    {["all", "daily", "weekly", "monthly", "yearly", "custom"].map((f) => (
                        <button
                            key={f}
                            className={`filter-btn ${filter === f ? "active" : ""}`}
                            onClick={() => {
                                setFilter(f);
                                if (f !== "custom") {
                                    setStartDate("");
                                    setEndDate("");
                                }
                            }}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>

                {/* ── Row 2: Filter dropdown (Mobile) ── */}
                <div className="filter-dropdown-wrapper mobile-only" style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginBottom: '12px' }}>
                    <button 
                        type="button" 
                        className="mobile-filter-trigger" 
                        onClick={() => setIsBottomSheetOpen(true)}
                        style={{
                            background: '#f8fafc',
                            color: '#3C0008',
                            border: '1px solid #3C0008',
                            borderRadius: '20px',
                            padding: '8px 16px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer'
                        }}
                    >
                        <Icon icon="mdi:filter-variant" style={{ fontSize: '18px' }} />
                        <span>{filter === 'all' ? 'All' : filter === 'daily' ? 'Today' : filter.charAt(0).toUpperCase() + filter.slice(1)}</span>
                    </button>
                </div>

                {isBottomSheetOpen && (
                    <div className="bottom-sheet-overlay" onClick={() => setIsBottomSheetOpen(false)}>
                        <div className="bottom-sheet-content" onClick={(e) => e.stopPropagation()}>
                            <div className="bottom-sheet-drag-handle"></div>
                            <div className="bottom-sheet-header">
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1e293b' }}>Select Filter</h3>
                                <button type="button" className="close-sheet-btn" onClick={() => setIsBottomSheetOpen(false)}>
                                    <Icon icon="mdi:close-circle" style={{ fontSize: '24px' }} />
                                </button>
                            </div>
                            <div className="bottom-sheet-options">
                                {[
                                    { value: 'all', label: 'All' },
                                    { value: 'daily', label: 'Today' },
                                    { value: 'weekly', label: 'Weekly' },
                                    { value: 'monthly', label: 'Monthly' },
                                    { value: 'yearly', label: 'Yearly' },
                                    { value: 'custom', label: 'Custom' },
                                ].map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`sheet-option-btn ${filter === opt.value ? 'active' : ''}`}
                                        onClick={() => {
                                            setFilter(opt.value);
                                            if (opt.value !== "custom") {
                                                setStartDate("");
                                                setEndDate("");
                                            }
                                            if (opt.value === "custom") {
                                                toast.info("Please select date range");
                                            }
                                            setIsBottomSheetOpen(false);
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {filter === opt.value && <Icon icon="mdi:check-circle" className="checked-icon" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Row 3: Custom date range (conditional) ── */}
                {filter === "custom" && (
                    <div className="custom-date-container">
                        <div className="date-input-group">
                            <span>From</span>
                            <input
                                type="date"
                                className="expense-date-input"
                                value={startDate}
                                onChange={(e) => handleDateChange("start", e.target.value)}
                            />
                        </div>
                        <div className="date-input-group">
                            <span>To</span>
                            <input
                                type="date"
                                className="expense-date-input"
                                value={endDate}
                                onChange={(e) => handleDateChange("end", e.target.value)}
                            />
                        </div>
                        {(startDate || endDate) && (
                            <button
                                className="clear-date-btn"
                                onClick={() => {
                                    setStartDate("");
                                    setEndDate("");
                                }}
                                title="Clear date range"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="Expense-cards">
                <div className="Expense-card card-expense">
                    <div className="expense-card-left">
                        <img src="/assets/total-expense.png" />
                    </div>
                    <div className="expense-card-right">
                        <h3>Total</h3>
                        <p>₹{totals?.overallTotalAmount ?? 0}</p>
                    </div>
                </div>
                <div className="Expense-card card-purchase">
                    <div className="expense-card-left">
                        <img src="/assets/shop-receipt.png" />
                    </div>
                    <div className="expense-card-right">
                        <h3>Billable</h3>
                        <p>₹{totals?.totalBillableAmount ?? 0}</p>
                    </div>
                </div>
                <div className="Expense-card card-return">
                    <div className="expense-card-left">
                        <img src="/assets/non-billable.png" />
                    </div>
                    <div className="expense-card-right">
                        <h3>Non Billable</h3>
                        <p>₹{totals?.totalNonBillableAmount ?? 0}</p>
                    </div>
                </div>

            </div>

        </>
    );
}
export default ExpenseHeader;